/**
 * Pure helpers behind the recipe editor and detail screens.
 *
 * The editor keeps every field as a string (what the person typed) and only
 * converts to the API shape on submit, so nothing is silently reformatted
 * while typing. Everything here is I/O-free and unit-tested.
 */
import { formatQuantity, parseIngredientLine, parseQuantity } from '@fewerbags/core';
import type {
  Course,
  CreateRecipeRequest,
  Cuisine,
  EquipmentKind,
  ImportDraft,
  RecipeDetail,
  RecipeVisibility,
  UnitKey,
} from '@fewerbags/schema';
import type { ZodError } from 'zod';

export type OvenMode = 'bake' | 'roast' | 'broil' | 'convection';

export interface IngredientRow {
  /** Stable React key; never sent to the API. */
  key: string;
  /** Quantity as typed: "2", "1 1/2", "2-3", or a whole line to be exploded on blur. */
  qtyText: string;
  unit: UnitKey;
  name: string;
  /** Set when the name came from the shared catalog; cleared when the name is edited by hand. */
  ingredientId: string | null;
  prepNote: string;
  optional: boolean;
  /** Verbatim source line, kept for review/re-parse. */
  raw: string | null;
}

export interface StepRow {
  key: string;
  text: string;
  durationText: string;
  attended: boolean;
  timer: boolean;
  /**
   * True once the timer has been decided — ticked/unticked by hand, or loaded
   * from a saved step that has a duration. Until then entering a duration
   * ticks the timer automatically (PLAN §9 #4); after it, the choice sticks.
   */
  timerTouched: boolean;
  canDoAhead: boolean;
  equipmentKind: '' | EquipmentKind;
  equipmentCount: string;
  ovenTempF: string;
  ovenMode: '' | OvenMode;
  /**
   * Ingredient rows this step uses, one `ingredientRefToken(row)` per link.
   * Tokens are stable across reordering/removal of ingredient rows and are
   * resolved to indexes into the submitted `ingredients` array on submit.
   */
  refIngredientIds: string[];
}

export interface RecipeFormState {
  title: string;
  description: string;
  servings: string;
  prepMin: string;
  cookMin: string;
  /** "Rest before serving (min)": the timeline finishes cooking this long before serving. */
  restMin: string;
  visibility: RecipeVisibility;
  tags: string[];
  cuisines: Cuisine[];
  courses: Course[];
  ingredients: IngredientRow[];
  steps: StepRow[];
}

let seq = 0;
/** Monotonic key for list rows — React needs identity that survives reordering. */
export function newKey(prefix = 'row'): string {
  seq += 1;
  return `${prefix}-${seq}`;
}

export function emptyIngredientRow(): IngredientRow {
  return {
    key: newKey('ing'),
    qtyText: '',
    unit: 'count',
    name: '',
    ingredientId: null,
    prepNote: '',
    optional: false,
    raw: null,
  };
}

export function emptyStepRow(): StepRow {
  return {
    key: newKey('step'),
    text: '',
    durationText: '',
    attended: true,
    timer: false,
    timerTouched: false,
    canDoAhead: false,
    equipmentKind: '',
    equipmentCount: '1',
    ovenTempF: '',
    ovenMode: '',
    refIngredientIds: [],
  };
}

export function emptyForm(defaultServings: number | null): RecipeFormState {
  return {
    title: '',
    description: '',
    servings: defaultServings === null ? '' : String(defaultServings),
    prepMin: '',
    cookMin: '',
    restMin: '',
    visibility: 'private',
    tags: [],
    cuisines: [],
    courses: [],
    ingredients: [emptyIngredientRow(), emptyIngredientRow(), emptyIngredientRow()],
    steps: [emptyStepRow()],
  };
}

// ------------------------------------------------------------- text ↔ rows --

function numberText(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

/** Render a stored quantity/range back into the editor's single text field. */
export function quantityText(qty: number | null, qtyMax: number | null): string {
  if (qty === null) return '';
  return qtyMax === null ? String(qty) : `${qty}-${qtyMax}`;
}

/**
 * Blur handler for the quantity box. A bare number (or range) is left alone;
 * anything with words in it is treated as a pasted ingredient line and split
 * into quantity / unit / name / prep. Fields the person already filled in win.
 */
export function applyQuantityText(row: IngredientRow): IngredientRow {
  const text = row.qtyText.trim();
  if (text === '') return row.qtyText === '' ? row : { ...row, qtyText: '' };
  const q = parseQuantity(text);
  // A clean number or range is already usable — leave it exactly as typed.
  if (q.qty !== null && q.rest === '') return row;
  const parsed = parseIngredientLine(text);
  const name = row.name.trim() === '' ? parsed.name : row.name;
  return {
    ...row,
    qtyText: quantityText(parsed.qty, parsed.qtyMax ?? null),
    unit: parsed.unit,
    name,
    ingredientId: name === row.name ? row.ingredientId : null,
    prepNote: row.prepNote.trim() === '' ? (parsed.prep ?? '') : row.prepNote,
    optional: row.optional || parsed.optional,
    raw: text,
  };
}

/** "Paste ingredients" textarea → one row per non-blank line. */
export function rowsFromPastedText(text: string): IngredientRow[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((line) => {
      const parsed = parseIngredientLine(line);
      return {
        key: newKey('ing'),
        qtyText: quantityText(parsed.qty, parsed.qtyMax ?? null),
        unit: parsed.unit,
        name: parsed.name,
        ingredientId: null,
        prepNote: parsed.prep ?? '',
        optional: parsed.optional,
        raw: parsed.raw,
      };
    });
}

export function formFromDetail(detail: RecipeDetail): RecipeFormState {
  return {
    title: detail.title,
    description: detail.description ?? '',
    servings: numberText(detail.servings),
    prepMin: numberText(detail.prepMin),
    cookMin: numberText(detail.cookMin),
    restMin: detail.restMin > 0 ? String(detail.restMin) : '',
    visibility: detail.visibility,
    tags: [...detail.tags],
    cuisines: [...detail.cuisines],
    courses: [...detail.courses],
    ingredients: detail.ingredients.map((line) => ({
      key: newKey('ing'),
      qtyText: quantityText(line.qty, line.qtyMax),
      unit: line.unit,
      name: line.name,
      ingredientId: line.ingredientId,
      prepNote: line.prepNote ?? '',
      optional: line.optional,
      raw: line.raw,
    })),
    steps: detail.steps.map((step) => ({
      key: newKey('step'),
      text: step.text,
      durationText: numberText(step.durationMin),
      attended: step.attended,
      timer: step.timer,
      timerTouched: savedTimerDecided(step.timer, step.durationMin),
      canDoAhead: step.canDoAhead,
      equipmentKind: step.equipment?.kind ?? '',
      equipmentCount: String(step.equipment?.count ?? 1),
      ovenTempF: numberText(step.equipment?.ovenTempF ?? null),
      ovenMode: step.equipment?.ovenMode ?? '',
      refIngredientIds: [...step.ingredientRefs],
    })),
  };
}

/** An approved import draft, pre-loaded into the editor (used by the review screen preview). */
export function formFromImportDraft(draft: ImportDraft): RecipeFormState {
  return {
    title: draft.title ?? '',
    description: draft.description ?? '',
    servings: numberText(draft.servings),
    prepMin: numberText(draft.prepMin),
    cookMin: numberText(draft.cookMin ?? draft.totalMin),
    restMin: '',
    visibility: 'private',
    tags: [...draft.tags],
    cuisines: [],
    courses: [],
    ingredients: draft.ingredients.map((line) => ({
      key: newKey('ing'),
      qtyText: quantityText(line.qty, line.qtyMax),
      unit: line.unit,
      name: line.ingredient,
      ingredientId: null,
      prepNote: line.prep ?? '',
      optional: line.optional,
      raw: line.raw,
    })),
    steps: draft.steps.map((step) => ({
      key: newKey('step'),
      text: step.text,
      durationText: numberText(step.durationMin),
      attended: step.attended,
      timer: step.timer,
      timerTouched: savedTimerDecided(step.timer, step.durationMin),
      canDoAhead: step.canDoAhead,
      equipmentKind: step.equipment?.kind ?? '',
      equipmentCount: String(step.equipment?.count ?? 1),
      ovenTempF: numberText(step.equipment?.ovenTempF ?? null),
      ovenMode: step.equipment?.ovenMode ?? '',
      refIngredientIds: [],
    })),
  };
}

/**
 * A saved step's timer counts as decided when it is on, or when the step
 * already has a duration (so an earlier "no timer" choice is respected).
 */
function savedTimerDecided(timer: boolean, durationMin: number | null): boolean {
  return timer || (durationMin !== null && durationMin > 0);
}

/** Minutes box changed: ticks the timer when a duration appears, unless the timer was decided. */
export function withStepDuration(row: StepRow, durationText: string): StepRow {
  if (row.timerTouched) return { ...row, durationText };
  const minutes = intOrNull(durationText);
  return { ...row, durationText, timer: minutes !== null && minutes > 0 };
}

/** Timer box ticked/unticked by hand: the choice sticks from now on. */
export function withStepTimer(row: StepRow, timer: boolean): StepRow {
  return { ...row, timer, timerTouched: true };
}

// ------------------------------------------------------------ rows → request --

function intOrNull(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n) : null;
}

/** A row counts as filled in once it has a name (or a catalog ingredient). */
export function isFilledIngredient(row: IngredientRow): boolean {
  return row.name.trim() !== '' || row.ingredientId !== null;
}

/**
 * Stable token for linking a step to an ingredient row: the catalog id once the
 * ingredient is resolved, otherwise the row's own React key. This lets a step
 * reference a brand-new ingredient name that has no catalog id yet, and keeps
 * working when ingredient rows are reordered (see `toRecipeRequest`).
 */
export function ingredientRefToken(row: IngredientRow): string {
  return row.ingredientId ?? row.key;
}

function stepEquipment(row: StepRow) {
  if (row.equipmentKind === '') return null;
  const count = intOrNull(row.equipmentCount);
  const temp = intOrNull(row.ovenTempF);
  return {
    kind: row.equipmentKind,
    count: count !== null && count > 0 ? count : 1,
    ...(temp !== null ? { ovenTempF: temp } : {}),
    ...(row.ovenMode !== '' ? { ovenMode: row.ovenMode } : {}),
  };
}

/**
 * Build the create/update body. Steps reference ingredients by index, so the
 * ids carried on step rows are mapped onto the surviving ingredient rows;
 * references to removed ingredients are dropped.
 */
export function toRecipeRequest(form: RecipeFormState): CreateRecipeRequest {
  const rows = form.ingredients.filter(isFilledIngredient);
  const indexByToken = new Map<string, number>();
  rows.forEach((row, i) => {
    indexByToken.set(ingredientRefToken(row), i);
  });

  return {
    title: form.title.trim(),
    description: form.description.trim() === '' ? null : form.description.trim(),
    servings: intOrNull(form.servings),
    prepMin: intOrNull(form.prepMin),
    cookMin: intOrNull(form.cookMin),
    restMin: Math.max(0, intOrNull(form.restMin) ?? 0),
    visibility: form.visibility,
    tags: form.tags,
    cuisines: form.cuisines,
    courses: form.courses,
    ingredients: rows.map((row) => {
      const q = parseQuantity(row.qtyText);
      const prep = row.prepNote.trim();
      return {
        ...(row.ingredientId ? { ingredientId: row.ingredientId } : { name: row.name.trim() }),
        qty: q.qty,
        qtyMax: q.qtyMax ?? null,
        unit: row.unit,
        prepNote: prep === '' ? null : prep,
        optional: row.optional,
        raw: row.raw,
      };
    }),
    steps: form.steps
      .filter((step) => step.text.trim() !== '')
      .map((step) => ({
        text: step.text.trim(),
        durationMin: intOrNull(step.durationText),
        attended: step.attended,
        timer: step.timer,
        canDoAhead: step.canDoAhead,
        equipment: stepEquipment(step),
        ingredientRefs: step.refIngredientIds
          .map((token) => indexByToken.get(token))
          .filter((i): i is number => i !== undefined),
      })),
  };
}

// ------------------------------------------------------------------- misc --

/** Move an item within a list, returning a new array (no-op when out of range). */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return [...list];
  }
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
}

/** Normalise a typed tag the way the API's `recipeTagSchema` does, or reject it. */
export function normaliseTag(raw: string): string | null {
  const tag = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (tag === '' || tag.length > 40) return null;
  return /^[a-z0-9][a-z0-9 _-]*$/.test(tag) ? tag : null;
}

export function scaleFactor(baseServings: number | null, target: number): number {
  if (baseServings === null || baseServings <= 0 || target <= 0) return 1;
  return target / baseServings;
}

/** "1 ½ cups" + "2 cups" → "1 ½–2 cups" (shared trailing unit words are shown once). */
export function collapseRange(low: string, high: string): string {
  const l = low.split(' ');
  const h = high.split(' ');
  let shared = 0;
  while (
    shared < l.length - 1 &&
    shared < h.length - 1 &&
    l[l.length - 1 - shared] === h[h.length - 1 - shared]
  ) {
    shared += 1;
  }
  return `${l.slice(0, l.length - shared).join(' ')}–${high}`;
}

/** Ingredient amount for the detail screen, scaled to the chosen servings. */
export function displayAmount(
  qty: number | null,
  qtyMax: number | null,
  unit: UnitKey,
  factor: number,
  system: 'us' | 'metric' = 'us',
): string {
  if (unit === 'to_taste') return 'to taste';
  if (qty === null) return '';
  const low = formatQuantity(qty * factor, unit, { system });
  if (qtyMax === null) return low;
  return collapseRange(low, formatQuantity(qtyMax * factor, unit, { system }));
}

export function formatMinutes(min: number | null): string {
  if (min === null || min <= 0) return '';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

/** "15 min prep · 45 min cook · 1 hr total" — parts that are unknown are left out. */
export function timeSummary(prepMin: number | null, cookMin: number | null): string {
  const parts: string[] = [];
  if (prepMin !== null && prepMin > 0) parts.push(`${formatMinutes(prepMin)} prep`);
  if (cookMin !== null && cookMin > 0) parts.push(`${formatMinutes(cookMin)} cook`);
  if (parts.length === 2) parts.push(`${formatMinutes((prepMin ?? 0) + (cookMin ?? 0))} total`);
  return parts.join(' · ');
}

const PATH_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  servings: 'Servings',
  prepMin: 'Prep time',
  cookMin: 'Cook time',
  restMin: 'Rest before serving',
  tags: 'Tags',
  cuisines: 'Cuisines',
  courses: 'Courses',
};

/** Turn schema issues into messages that name the row a person can actually find. */
export function describeIssues(error: ZodError): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const issue of error.issues) {
    const [head, index, field] = issue.path;
    let label: string;
    if (head === 'ingredients' && typeof index === 'number') {
      label = `Ingredient ${index + 1}${field ? ` (${String(field)})` : ''}`;
    } else if (head === 'steps' && typeof index === 'number') {
      label = `Step ${index + 1}${field ? ` (${String(field)})` : ''}`;
    } else {
      label = PATH_LABELS[String(head)] ?? String(head ?? 'Recipe');
    }
    const message = `${label}: ${issue.message}`;
    if (!seen.has(message)) {
      seen.add(message);
      out.push(message);
    }
  }
  return out;
}
